/* eslint-disable no-underscore-dangle */
import {
  Module,
  VuexModule,
  Mutation,
  Action,
  getModule,
} from 'vuex-module-decorators';

import {
  User,
  Session,
  UserRole,
  ApplicationRecord,
  PropertyUser,
  SurveyProperty,
  UserFavourite,
} from '@/api';

import store from '@/store';
import i18n from '@/plugins/vue-i18n';

import nextQuery from '@/nextQuery';
import dayjs from 'dayjs';
import Cookies from 'js-cookie';

@Module({ dynamic: true, namespaced: true, name: 'auth', store })
class AuthModule extends VuexModule {
  ready = false;

  user: User | null = null;

  session: Session | null = null;

  _mappedFavourites: { [fmId: string]: UserFavourite } = {};

  myProperties: SurveyProperty[] = [];

  loginHooks: Array<
    (loginData: { user: User; session: Session }) => Promise<void>
  > = [];

  logoutHooks: Array<() => Promise<void>> = [];

  canAccessRoute = false;

  showWelcomeDialog = false;

  defaultPropertyId: string | null = null;

  tosDate = dayjs(new Date(2022, 7, 15));

  // all the users available to me (as a regular user)
  get availableUsers() {
    const users: User[] = [];
    this.myProperties.forEach(property => {
      users.push(property.owner, ...property.propertyUsers.map(pu => pu.user));
    });
    return users;
  }

  // all property users that are me
  get myPropertyUsers() {
    const propertyUsers: PropertyUser[] = [];
    this.myProperties.forEach(property => {
      propertyUsers.push(...property.propertyUsers.filter(pu => pu.user.id));
    });
    return propertyUsers;
  }

  get isLoggedIn() {
    return !!this.ready && !!this.user && !!this.session;
  }

  get canAccess() {
    return (roles: string[]): boolean => {
      if (roles.length === 0) {
        return true;
      }
      return roles.some(role => !!this.user && this.user.roles.includes(role));
    };
  }

  get canAssessSurveys() {
    return (property: SurveyProperty): boolean => {
      if (this.canAccess([UserRole.admin, UserRole.manager])) {
        return true;
      }
      if (this.user && this.user.id === property.owner.id) {
        return true;
      }
      return this.myPropertyUsers.some(
        propertyUser =>
          propertyUser.surveyProperty.id === property.id &&
          propertyUser.canAssessSurvey,
      );
    };
  }

  get canCreateSurveys() {
    return (property: SurveyProperty): boolean => {
      if (this.isAdmin) {
        return true;
      }
      if (this.user && this.user.id === property.owner.id) {
        return true;
      }
      return this.myPropertyUsers.some(
        propertyUser =>
          propertyUser.surveyProperty.id === property.id &&
          propertyUser.canCreateSurvey,
      );
    };
  }

  get isAdmin() {
    return this.user && this.canAccess([UserRole.admin, UserRole.manager]);
  }

  get isExpert() {
    return (
      this.user &&
      this.canAccess([UserRole.admin, UserRole.manager, UserRole.expert])
    );
  }

  get userFavouriteById() {
    return (id: string) => this._mappedFavourites[id] || null;
  }

  get termsAgreed() {
    return (
      this.user &&
      this.user.termsAgreed &&
      dayjs(this.user.termsAgreed).diff(this.tosDate) > 0
    );
  }

  // mutations

  @Mutation
  setReady() {
    this.ready = true;
  }

  @Mutation
  addLoginHook(
    hook: (loginData: { user: User; session: Session }) => Promise<void>,
  ) {
    this.loginHooks.push(hook);
  }

  @Mutation
  addLogoutHook(hook: () => Promise<void>) {
    this.logoutHooks.push(hook);
  }

  @Mutation
  setUser(user: User | null) {
    this.user = user;
  }

  @Mutation
  setSession(session: Session | null) {
    this.session = session;
  }

  @Mutation
  setMyProperties(properties: SurveyProperty[]) {
    this.myProperties = properties;
  }

  @Mutation
  setUserFavourites(userFavourites: UserFavourite[]) {
    const favourites: { [fmId: string]: UserFavourite } = {};
    userFavourites.forEach(uf => {
      favourites[uf.faunaMedia.id as string] = uf;
    });
    this._mappedFavourites = favourites;
  }

  @Mutation
  setCanAccessRoute(val: boolean) {
    this.canAccessRoute = val;
  }

  @Mutation
  setShowWelcomeDialog(val: boolean) {
    this.showWelcomeDialog = val;
  }

  @Mutation
  setDefaultPropertyId(id: string | null) {
    this.defaultPropertyId = id;
  }

  // actions

  @Action({ rawError: true })
  clearAuth() {
    this.setSession(null);
    this.setUser(null);
    this.setMyProperties([]);
    ApplicationRecord.jwt = undefined;
  }

  @Action({ rawError: true })
  async setUserInfo({ user, session }: { user: User; session: Session }) {
    // do some other stuff??
    // user and session
    await this.setUserSession({ user, session });
  }

  @Action({ rawError: true })
  async setUserSession({ user, session }: { user: User; session: Session }) {
    this.setSession(session);
    this.setUser(user);
  }

  @Action({ rawError: true })
  async login({ email, password }: { email: string; password: string }) {
    try {
      const session = new Session({ email, password });
      await session.save();

      if (session && session.id && session.user.id) {
        ApplicationRecord.jwt = session.token;
        const user = (await User.find(session.user.id)).data;

        await this.setUserInfo({ user, session });
        await this.onLogin();
      }
    } catch (e) {
      this.clearAuth();
      throw e;
    }
  }

  @Action({ rawError: true })
  async registerUser({
    name,
    email,
    password,
  }: {
    name: string;
    email: string;
    password: string;
  }) {
    try {
      const user = new User({ name, email, password });
      await user.save();

      const session = new Session({ email, password });
      await session.save();

      if (session && session.id && session.user.id) {
        ApplicationRecord.jwt = session.token;

        await this.setUserSession({ user, session });
        await this.onLogin();
      }
    } catch (e) {
      this.clearAuth();
      throw e;
    }
  }

  @Action({ rawError: true })
  async getMyProperties() {
    if (this.isExpert) {
      this.setMyProperties([]);
      return;
    }

    const properties = (
      await SurveyProperty.select(['id', 'name', 'owner', 'propertyUsers'])
        .includes(['owner', 'propertyUsers'])
        .where({ skip_stats: true })
        .all()
    ).data;
    this.setMyProperties(properties);
  }

  @Action({ rawError: true })
  async getUserFavourites() {
    if (!this.user) {
      return;
    }
    const userFavourites = (
      await UserFavourite.where({ user: this.user.id }).all()
    ).data;
    this.setUserFavourites(userFavourites);
  }

  @Action({ rawError: true })
  async getCurrentUser() {
    try {
      const session = (await Session.first()).data;

      if (session && session.id && session.user.id) {
        const user = (await User.find(session.user.id)).data;

        await this.setUserInfo({ user, session });
        await this.onLogin();
      }
    } catch (e) {
      this.clearAuth();
      throw e;
    }
  }

  @Action({ rawError: true })
  async logout() {
    const session = new Session();
    try {
      await session.destroy();
    } finally {
      await this.onLogout();
      ApplicationRecord.jwt = undefined;
      window.location.href = process.env.BASE_URL;
    }
  }

  @Action({ rawError: true })
  async init() {
    try {
      await this.getCurrentUser();
      // logged in
    } catch {
      // not logged in
    } finally {
      // add redirect on 401 middleware
      ApplicationRecord.middlewareStack.afterFilters.push(response => {
        if (response.status === 401) {
          ApplicationRecord.jwt = undefined;
          window.location.href = `${
            process.env.BASE_URL
          }login?next=${nextQuery()}`;
        }
      });

      // set ready
      this.setReady();
    }
  }

  // call all other modules that need login data
  @Action({ rawError: true })
  async onLogin() {
    const { user, session } = this;
    if (!user || !session) {
      throw new Error(i18n.t('authModule.alerts.missingUserSession') as string);
    }
    await this.getMyProperties();
    await this.getUserFavourites();
    await Promise.all(this.loginHooks.map(hook => hook({ user, session })));
  }

  // call all other modules that need logout data
  @Action({ rawError: true })
  async onLogout() {
    await Promise.all(this.logoutHooks.map(hook => hook()));
  }

  @Action({ rawError: true })
  async setTerms() {
    if (!this.user) {
      throw new Error('User not set');
    }
    const user = new User({
      id: this.user.id,
    });
    user.isPersisted = true;
    user.termsAgreed = dayjs().toISOString();
    await user.save();

    this.user.termsAgreed = user.termsAgreed;
  }

  @Action({ rawError: true })
  async setProfileInfo({
    username,
    isPublic,
  }: {
    username: string;
    isPublic: boolean;
  }) {
    if (!this.user) {
      throw new Error('User not set');
    }
    const user = new User({
      id: this.user.id,
    });
    user.isPersisted = true;
    user.name = username;
    user.isPublic = isPublic;
    await user.save();

    Cookies.set('wt-profile', 'true', {
      expires: dayjs()
        .add(30, 'days')
        .toDate(),
    });

    // eslint-disable-next-line no-self-assign
    window.location.href = window.location.href;
  }
}

export default getModule(AuthModule, store);
